import { useState } from "react";
import { Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { GitBranch } from "lucide-react";
import FlagBanner from "./FlagBanner";
import PhaseBlock from "./PhaseBlock";
import SyncPlanHeader from "./SyncPlanHeader";
import { SyncMode, SyncPlanConfig } from "./types";

const SyncPlanVisualization = ({
  config,
  integrationID,
}: {
  config: SyncPlanConfig;
  integrationID: string;
}) => {
  const [syncMode, setSyncMode] = useState<SyncMode>("subsequent");

  const hasAnyInitialOverride = config.blueprint_priorities.some(
    (entry) => entry.should_run_different_initial_sync_blueprint !== null
  );

  const byPriority = new Map<number, typeof config.blueprint_priorities[number][]>();
  for (const entry of config.blueprint_priorities) {
    const group = byPriority.get(entry.priority) ?? [];
    group.push(entry);
    byPriority.set(entry.priority, group);
  }
  const phases = Array.from(byPriority.entries()).sort(([a], [b]) => a - b);

  const activeFlags = [
    config.enforce_strict_priority_ordering && {
      title: "Enforce strict priority ordering = TRUE",
      subtitle: "N must finish before N+1 starts",
      color: "orange" as const,
    },
    config.require_sequential_success && {
      title: "Require sequential success",
      subtitle: "any non-200 cancels the rest and retries in next cycle",
      color: "yellow" as const,
    },
    config.only_initial_sync && {
      title: "Only initial sync",
      subtitle: "this plan only runs on first sync",
      color: "blue" as const,
    },
  ].filter(Boolean) as { title: string; subtitle: string; color: "orange" | "yellow" | "blue" }[];

  return (
    <div>
      <SyncPlanHeader config={config} />

      {/* Sync mode toggle */}
      <div className="flex items-center gap-2 mb-6">
        {hasAnyInitialOverride ? (
          <>
            <Button
              size="sm"
              variant={
                syncMode === "subsequent" ? ButtonVariant.PrimaryGray : ButtonVariant.SecondaryGray
              }
              onClick={() => setSyncMode("subsequent")}
            >
              Subsequent Sync
            </Button>
            <Button
              size="sm"
              variant={
                syncMode === "initial" ? ButtonVariant.PrimaryGray : ButtonVariant.SecondaryGray
              }
              onClick={() => setSyncMode("initial")}
              rightIcon={<GitBranch size={13} />}
            >
              Initial Sync
            </Button>
          </>
        ) : (
          <Button size="sm" variant={ButtonVariant.PrimaryGray} onClick={() => {}}>
            Initial &amp; Subsequent Syncs
          </Button>
        )}
      </div>

      {activeFlags.length > 0 && (
        <div className="flex flex-col gap-2 mb-6">
          {activeFlags.map((flag) => (
            <FlagBanner key={flag.title} {...flag} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-[auto_1fr] gap-x-4">
        {phases.map(([priority, entries], index) => (
          <PhaseBlock
            key={priority}
            priority={priority}
            entries={entries}
            maxConcurrentBlueprints={config.maximum_concurrent_blueprints}
            integrationID={integrationID}
            syncMode={syncMode}
            isLast={index === phases.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default SyncPlanVisualization;
